import styled from "styled-components";
import { tokens } from "../../public/tokens";

export const sizes = {
    xs: {
        padding: `0 ${tokens.globals["spacing-2"]}`,
        fontSize: tokens.typography.fontSize["md-12px"],
        lineHeight: "16px",
        borderRadius: tokens.globals["border-radius"],
        height: tokens.globals["spacing-7"],
    },
    sm: {
        padding: `0 ${tokens.globals["spacing-2-5"]}`,
        fontSize: tokens.typography.fontSize["md-12px"],
        lineHeight: "16px",
        borderRadius: tokens.globals["border-radius"],
        height: tokens.globals["spacing-8"],
    },
    md: {
        padding: `0 ${tokens.globals["spacing-2-5"]}`,
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "20px",
        borderRadius: tokens.globals["border-radius"],
        height: tokens.globals["spacing-9"],
    },
    lg: {
        padding: `0 ${tokens.globals["spacing-2-5"]}`,
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "20px",
        borderRadius: tokens.globals["border-radius"],
        height: tokens.globals["spacing-10"],
    },
};

export const variants = {
    default: {
        height: null,
        padding: null,
        borderRadius: null,
        fontSize: null,
        lineHeight: null,
        fontWeight: tokens.typography.fontWeights.regular,
        placeholderWeight: tokens.typography.fontWeights.regular,
        showDivider: true,
        chevronSize: tokens.typography.fontSize["base-16px"],
        gap: tokens.globals["spacing-1-5"],
        endGap: tokens.globals["spacing-2-5"],
        listOffset: tokens.globals["spacing-1"],
    },
    filter: {
        height: "35px",
        padding: `0 ${tokens.globals["spacing-2-5"]}`,
        borderRadius: tokens.globals["border-radius"],
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "20px",
        fontWeight: tokens.typography.fontWeights.semibold,
        placeholderWeight: tokens.typography.fontWeights.semibold,
        showDivider: false,
        chevronSize: tokens.typography.fontSize["lg-14px"],
        gap: tokens.globals["spacing-1-5"],
        endGap: tokens.globals["spacing-1-5"],
        listOffset: tokens.globals["spacing-1"],
    },
    bulk: {
        height: "35px",
        padding: `0 ${tokens.globals["spacing-2-5"]}`,
        borderRadius: tokens.globals["border-radius"],
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "20px",
        fontWeight: tokens.typography.fontWeights.regular,
        placeholderWeight: tokens.typography.fontWeights.regular,
        showDivider: false,
        chevronSize: tokens.typography.fontSize["base-16px"],
        gap: tokens.globals["spacing-1-5"],
        endGap: tokens.globals["spacing-2-5"],
        listOffset: tokens.globals["spacing-1"],
        dropUp: true,
    },
};

export function getVariant(variant) {
    return variants[variant] || variants.default;
}

/*
 * $autoWidth (opt-in, default false so SelectDropdown/the default MultiSelectDropdown variant
 * keep today's fill-the-column behavior): shrinks the field to its trigger's own content instead
 * of stretching to 100% of its container - needed so a filter-style trigger sits inline with its
 * toolbar siblings (Search/Term/Year/Reset) instead of always claiming a full row.
 */
export const StyledInputGroup = styled.div(({ $autoWidth = false }) => ({
    width: $autoWidth ? "auto" : "100%",
    alignSelf: $autoWidth ? "auto" : "stretch",
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "flex-start",
}));

export const StyledDefaultInput = styled.div(({ $autoWidth = false }) => ({
    width: $autoWidth ? "auto" : "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: tokens.globals["spacing-px"],
}));

export const StyledTop = styled.div({
    width: "100%",
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2"],
});

export const StyledLabel = styled.label({
    margin: 0,
    flex: "1 0 0",
    minWidth: 0,
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "20px",
});

export const StyledRequired = styled.span({
    color: tokens.colors.ui.destructive,
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "20px",
    marginLeft: tokens.globals["spacing-0-5"],
    position: "relative",
    top: "2px",
});

export const StyledInfoIcon = styled.i({
    display: "inline-flex",
    alignItems: "center",
    lineHeight: 1,
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["md-12px"],
    cursor: "help",
});

export const StyledSelectWrapper = styled.div(({ $autoWidth = false }) => ({
    width: $autoWidth ? "auto" : "100%",
    position: "relative",
}));

export const StyledSelectValue = styled.div(({ $size = "lg", $variant = "default", $disabled = false, $error = false, $open = false }) => {
    const size = sizes[$size] || sizes.lg;
    const variant = getVariant($variant);

    const height = variant.height || size.height;
    const padding = variant.padding || size.padding;
    const borderRadius = variant.borderRadius || size.borderRadius;
    const fontSize = variant.fontSize || size.fontSize;
    const lineHeight = variant.lineHeight || size.lineHeight;

    return {
        width: "100%",
        height,
        minHeight: height,
        padding,
        backgroundColor: $disabled ? tokens.colors.neutral.grey["100"] : tokens.colors.surfaces["container-background"],
        border: `${tokens.globals["spacing-px"]} solid ${$error ? tokens.colors.ui.destructive : ($open ? tokens.colors.ui.ring : tokens.colors.surfaces["border-divider"])}`,
        borderRadius,
        /*
         * "filter" is the Figma "Buttons" component (Back/Active Gradebook/Website
         * Page share this look) and takes its card/button drop-shadow; every other
         * variant matches the reference form field (common/forms/FormInput / Career
         * Library) which has no box-shadow — just the 1px border — so a ring here
         * would draw a second, offset outline alongside it ("double border").
         */
        boxShadow: $variant === "filter" ? tokens.effects.effects.shadow.default : "none",
        display: "flex",
        alignItems: "center",
        gap: variant.gap,
        cursor: $disabled ? "not-allowed" : "pointer",
        opacity: $disabled ? 0.6 : 1,
        color: tokens.colors.text.primary,
        fontFamily: tokens.typography.fontFamilies["albert-sans"],
        fontSize,
        lineHeight,
        transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
        boxSizing: "border-box",

        "&:hover": $disabled ? {} : ($variant === "filter" ? {
            background: tokens.colors.neutral.grey["100"],
            borderColor: tokens.colors.surfaces["border-divider"],
        } : {
            borderColor: $open ? tokens.colors.ui.ring : tokens.colors.neutral.grey["500"],
        }),

        "&:focus-visible": $disabled ? {} : {
            outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.ui.ring}`,
            outlineOffset: tokens.globals["spacing-px"],
        },
    };
});

export const StyledValueText = styled.span(({ $placeholder = false, $variant = "default" }) => {
    const variant = getVariant($variant);

    return {
        flex: "1 1 0",
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: $placeholder ? ($variant === "filter" ? tokens.colors.text.primary : tokens.colors.text.secondary) : tokens.colors.text.primary,
        fontFamily: tokens.typography.fontFamilies["albert-sans"],
        fontWeight: $placeholder ? variant.placeholderWeight : variant.fontWeight,
    };
});

export const StyledEnd = styled.span(({ $variant = "default" }) => {
    const variant = getVariant($variant);

    return {
        height: tokens.globals["spacing-7"],
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: variant.endGap,
        flexShrink: 0,
    };
});

export const StyledClearButton = styled.button({
    width: tokens.globals["spacing-5"],
    height: tokens.globals["spacing-5"],
    padding: 0,
    border: "none",
    background: "transparent",
    color: tokens.colors.text.secondary,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: tokens.globals["border-radius"],
    cursor: "pointer",

    "&:hover": {
        background: tokens.colors.neutral.grey.Alpha["10"],
        color: tokens.colors.text.primary,
    },

    "&:focus-visible": {
        outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.brand.blue["500"]}`,
        outlineOffset: tokens.globals["spacing-px"],
    },
});

export const StyledDivider = styled.span({
    width: tokens.globals["spacing-px"],
    alignSelf: "stretch",
    background: tokens.colors.surfaces["border-divider"],
});

export const StyledLeadingIcon = styled.i(({ $variant = "default" }) => {
    const variant = getVariant($variant);

    return {
        color: tokens.colors.text.primary,
        fontSize: variant.chevronSize,
        lineHeight: "16px",
        flexShrink: 0,
    };
});

export const StyledChevron = styled.i(({ $open = false, $variant = "default" }) => {
    const variant = getVariant($variant);

    return {
        color: tokens.colors.text.primary,
        fontSize: variant.chevronSize,
        lineHeight: "16px",
        transition: "transform 0.2s ease-in-out",
        transform: $open ? "rotate(180deg)" : "rotate(0deg)",
        flexShrink: 0,
    };
});

export const StyledDropdownList = styled.ul(({ $size = "lg", $variant = "default", $top = 0, $left = 0, $width = 0, $dropUp = false, $autoWidth = false }) => {
    const size = sizes[$size] || sizes.lg;
    const variant = getVariant($variant);

    return {
        position: "fixed",
        top: `${$top}px`,
        left: `${$left}px`,
        width: $autoWidth ? "max-content" : `${$width}px`,
        transform: $dropUp ? "translateY(-100%)" : undefined,
        minWidth: `${$width}px`,
        maxWidth: $autoWidth ? "260px" : undefined,
        maxHeight: "220px",
        overflowY: "auto",
        margin: 0,
        padding: `${tokens.globals["spacing-1"]} 0`,
        backgroundColor: tokens.colors.surfaces["container-background"],
        border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
        borderRadius: variant.borderRadius || size.borderRadius,
        boxShadow: tokens.effects.effects.shadow.hover,
        zIndex: 3000,
        listStyle: "none",
        boxSizing: "border-box",
    };
});

export const StyledDropdownItem = styled.li(({ $selected = false, $highlighted = false, $disabled = false }) => ({
    minHeight: "35px",
    padding: `${tokens.globals["spacing-1-5"]} ${tokens.globals["spacing-3"]}`,
    cursor: $disabled ? "not-allowed" : "pointer",
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.regular,
    color: $disabled ? tokens.colors.text.disabled : ($selected ? tokens.colors.brand.blue["500"] : tokens.colors.text.primary),
    backgroundColor: $highlighted && !$disabled ? tokens.colors.neutral.grey["100"] : "transparent",
    opacity: $disabled ? 0.6 : 1,
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",

    "&:hover": $disabled ? {} : { backgroundColor: tokens.colors.neutral.grey["100"] },
}));

export const StyledOptionContent = styled.span({
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-1"],
});

export const StyledOptionIcon = styled.i(({ $tone = "default" }) => {
    const tones = {
        success: tokens.colors.states.success["500"],
        muted: tokens.colors.neutral.grey["400"],
        warning: tokens.colors.states.warning["500"],
        destructive: tokens.colors.ui.destructive,
        default: tokens.colors.text.primary,
    };

    return {
        width: tokens.globals["spacing-3-5"],
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1,
        color: tones[$tone] || tones.default,
        fontSize: tokens.typography.fontSize["md-12px"],
        textAlign: "center",
    };
});

export const StyledOptionLabel = styled.span(({ $emphasis = false }) => ({
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: $emphasis ? tokens.colors.brand.blue["500"] : tokens.colors.text.primary,
    fontWeight: $emphasis ? tokens.typography.fontWeights.bold : tokens.typography.fontWeights.regular,
}));

export const StyledStateItem = styled.li({
    padding: `${tokens.globals["spacing-sm"]} ${tokens.globals["spacing-3"]}`,
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
});

export const StyledMessage = styled.span(({ $error = false }) => ({
    color: $error ? tokens.colors.ui.destructive : tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: $error ? tokens.typography.fontWeights.medium : tokens.typography.fontWeights.regular,
    lineHeight: "16px",
}));
